import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DataSeries } from '@next/core-lib/models';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import * as refValSheetActions from '../../../reference-value-sheet/store/reference-value-sheet.actions';
import { PerformanceModuleState, WeeklyKpiDataResponse } from '../../models/performance.model';
import { PoultryNextRearingService } from '../../services/poultrynext-rearing.service';
import * as performanceActions from '../actions/performance.actions';
import { getAvailableSections, getPerformanceModuleState } from '../selectors/performance.selector';

@Injectable()
export class PerformanceEffects {
  constructor(
    private actions$: Actions,
    private store: Store<PerformanceModuleState>,
    private readonly pnRearingService: PoultryNextRearingService,
  ) {}

  setDefaultSectionsWhenCycleSelected$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(performanceActions.setSelectedCycleId),
      withLatestFrom(this.store.select(getAvailableSections)),
      skipWhile(([cycle, availableSections]) => !cycle.cycleId || availableSections?.length === 0),
      switchMap(([cycle, availableSections]) =>
        of(
          performanceActions.setSelectedSectionIds({
            sectionIds: availableSections.map((s) => s.id),
          }),
          performanceActions.loadObservationsForCycleRequest({ cycleId: cycle.cycleId }),
        ),
      ),
    ),
  );

  loadObservationsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(performanceActions.loadObservationsForCycleRequest),
      debounceTime(100),
      mergeMap(({ cycleId }) =>
        this.pnRearingService.getObservations(cycleId).pipe(
          map((observations) =>
            performanceActions.loadObservationsForCycleSuccess({ observations }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              performanceActions.loadObservationsForCycleFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertObservationsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(performanceActions.upsertbservationsForCycleRequest),
      debounceTime(100),
      filter(({ observations }) => observations.length > 0),
      mergeMap(({ observations }) =>
        this.pnRearingService
          .upsertObservations(
            observations,
            observations[0].cycleId,
            observations[0].barnId,
            observations[0].sectionId,
          )
          .pipe(
            map(() => performanceActions.upsertObservationsForCycleSuccess({ observations })),
            catchError((error: HttpErrorResponse) =>
              of(
                performanceActions.loadObservationsForCycleFailed({ error }),
                NotificationActions.errorSnackbar({ message: error.message }),
              ),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  filterChanged$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        performanceActions.setSelectedCycleId,
        performanceActions.setSelectedSectionIds,
        performanceActions.setSelectedKpiParameters,
        performanceActions.upsertObservationsForCycleSuccess,
      ),
      withLatestFrom(this.store.select(getPerformanceModuleState)),
      switchMap(([_, state]) => {
        if (
          state.selectedCycleId > 0 &&
          state.selectedSectionIds.length > 0 &&
          state.selectedKpiParameters.length > 0
        ) {
          return of(
            performanceActions.loadWeeklyKpiResultsRequest({
              cycleId: state.selectedCycleId,
              weeklyKpiDataRequest: {
                parameters: state.selectedKpiParameters,
                sectionIds: state.selectedSectionIds,
              },
            }),
          );
        } else {
          return of(
            performanceActions.loadWeeklyKpiResultsSuccess({
              results: [],
            }),
          );
        }
      }),
    ),
  );

  loadWeeklyKpiResults$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(performanceActions.loadWeeklyKpiResultsRequest),
      exhaustMap((request) =>
        this.pnRearingService.getWeeklyKpiData(request.cycleId, request.weeklyKpiDataRequest).pipe(
          switchMap((weeklyKpiDataResponse: WeeklyKpiDataResponse) =>
            of(
              performanceActions.loadWeeklyKpiResultsSuccess({
                results: weeklyKpiDataResponse.results,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
            return of(performanceActions.loadWeeklyKpiResultsFailed({ error }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadTimeSeries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(performanceActions.loadTimeSeriesRequest),
      exhaustMap((request) =>
        of(
          performanceActions.loadDailyKpiTimeSeriesRequest({
            cycleId: request.cycleId,
            parameter: request.parameter,
            sectionIds: request.sectionIds,
          }),
          refValSheetActions.loadRefValSheetDataSeriesRequest({ ids: request.rvsIds }),
        ),
      ),
    ),
  );

  loadDailyKpiTimeSeries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(performanceActions.loadDailyKpiTimeSeriesRequest),
      exhaustMap((request) =>
        this.pnRearingService
          .getDailyKpiTimeseries(request.cycleId, request.parameter, request.sectionIds)
          .pipe(
            switchMap((timeSeries: DataSeries<number, number>) =>
              of(
                performanceActions.loadDailyKpiTimeSeriesSuccess({
                  timeSeries,
                }),
              ),
            ),
            catchError((error: HttpErrorResponse) => {
              this.store.dispatch(CoreActions.setLoading({ loading: false }));
              return of(performanceActions.loadDailyKpiTimeSeriesFailed({ error }));
            }),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );
}
